import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { DataTable, Pagination } from '@sdir/sds';
import { TableColumn } from '@sdir/utilities/lib/types/tables';

export const DEFAULT_PAGESIZE = 25;
export const DEFAULT_ORDERBY = 'createdAt';
export const DEFAULT_DESC = true;

interface ApplicationTableProps {
  data: any;
  columns: TableColumn[];
  pageSize?: number;
  sortableKeys?: string[] | '*';
  currentPage?: number;
  totalCount?: number;
  loading: boolean;
  refreshData: (payload) => void;
}

const ApplicationTable: React.FC<ApplicationTableProps> = ({
  data,
  columns,
  loading,
  pageSize = DEFAULT_PAGESIZE,
  sortableKeys = '*',
  currentPage = 1,
  totalCount = 0,
  refreshData,
}) => {
  const intl = useIntl();
  const nrOfPages = Math.ceil((totalCount || 0) / pageSize);

  const handleChangePage = useCallback(
    (pageNr: number) => {
      const skip = pageNr * pageSize;
      refreshData?.({ skip, take: pageSize, page: pageNr });
    },
    [pageSize]
  );

  const handleSorting = useCallback((columnOption: any) => {
    refreshData?.({ orderBy: columnOption.key, direction: columnOption.direction });
  }, []);

  return (
    <>
      <StyledDataTable
        className="application-table"
        columns={columns}
        data={data}
        sortableKeys={sortableKeys}
        collapsable
        collapsableText={[
          intl.formatMessage({ id: 'application.table.showdetails' }),
          intl.formatMessage({ id: 'application.table.hidedetails' }),
        ]}
        onHeaderClick={handleSorting}
      />
      <BottomBar>
        <div />
        {nrOfPages > 1 && (
          <Pagination
            currentPage={currentPage}
            disabled={loading}
            newPageClick={handleChangePage}
            nrOfPages={nrOfPages}
          />
        )}
        <TableCount>
          {intl.formatMessage({ id: 'application.table.count' })}: {totalCount || 0}
        </TableCount>
      </BottomBar>
    </>
  );
};

export default ApplicationTable;

const BottomBar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin: 20px 0;
`;

const TableCount = styled.div`
  font-weight: bold;
`;

const StyledDataTable = styled(DataTable)`
  background-color: transparent;
  thead th button {
    padding-left: 0;
  }

  & .datepicker-row {
    width: 225px;
    height: 4rem;
    #select-payment-range-start {
      padding: 0 8px;
      font-size: 14px;
    }
    #select-payment-range-end {
      padding: 0 0 0 5px;
      font-size: 14px;
    }
    .icon-wrapper {
      padding: 0 8px;
      width: 32px;
    }
  }

  #select-seafarer,
  #select-status,
  #select-nationality {
    height: 4rem;
  }
  #select-seafarer > button,
  #select-status > button,
  #select-nationality > button {
    padding: 0 1rem;
    span {
      font-size: 14px;
    }
  }
  #select-seafarer div[open],
  #select-status div[open],
  #select-nationality div[open] {
    width: max-content;
    max-width: 250px;
  }

  &.application-table > table > tbody > tr {
    &:first-child {
      &:hover {
        background: transparent;
      }
      &.open-row {
        background: transparent;
        & + tr {
          display: none;
        }
      }

      td:last-child {
        display: none;
      }

      @media screen and (max-width: ${({ theme }) => theme.screen.large}) {
        display: none;
      }
    }
  }

  tbody tr {
    border-top: 1px solid ${({ theme }) => theme.colors.primary8};
    min-height: 40px;

    &:last-child {
      border-bottom: 1px solid ${({ theme }) => theme.colors.primary8};
    }

    td:last-child > button {
      min-width: 125px;
    }
  }

  && td.open-row-content {
    padding-left: 3rem;
  }

  .application-child-table {
    background-color: transparent;
    padding: 0;

    thead tr,
    tbody tr {
      border-top: none;
      border-bottom: none;
    }
  }
`;
